// WP-3.2 (M2 QT-1..6, S-22 `/quotes/:id`): versions accordion (QT-5); state buttons follow
// QT-1 transitions only (only the legal next state(s) ever render as a button — there is no
// generic "change status" control); Convert ▾: Project · Contract · Receivable (QT-4).
// WP-7.3 (QT-6) adds "Share summary (PDF)" — available at any status, not gated to one QT-1
// transition, since sharing a summary is not itself a state change.
import { formatMoney, type CurrencyCode, type QuotationConvertTarget } from '@pfos/engine';
import type { QuotationPricing } from '@pfos/shared';
import { ArrowLeft, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { EntityTagSection } from '../components/EntityTagSection';
import {
  AmountField,
  Field,
  ghostButtonInlineClass,
  inputClass,
  primaryButtonInlineClass,
  selectClass,
} from '../components/FormKit';
import {
  useAcceptQuotation,
  useConvertQuotation,
  useExpireQuotation,
  useQuotation,
  useRejectQuotation,
  useRevisePricing,
  useSendQuotation,
} from '../lib/quotations';
import { downloadQuotationPdf } from '../lib/pdf';

function pricingLabel(pricing: QuotationPricing, currency: CurrencyCode): string {
  if (pricing.kind === 'fixed') return formatMoney({ currency, minorUnits: pricing.amountMinor });
  const min = formatMoney({ currency, minorUnits: pricing.minMinor });
  const max = formatMoney({ currency, minorUnits: pricing.maxMinor });
  return `${min}–${max}`;
}

const CONVERT_TARGETS: { value: QuotationConvertTarget; label: string }[] = [
  { value: 'project', label: 'Project' },
  { value: 'contract', label: 'Contract' },
  { value: 'receivable', label: 'Receivable' },
];

function VersionsAccordion({
  versions,
  currency,
}: {
  versions: { pricing: QuotationPricing; supersededAt: string }[];
  currency: CurrencyCode;
}): React.JSX.Element | null {
  const [open, setOpen] = useState(false);
  if (versions.length === 0) return null;
  return (
    <div className="mb-4 rounded-[var(--radius-card)] border border-border bg-surface p-3">
      <button
        type="button"
        className="flex items-center gap-1 text-sm font-medium text-ink"
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        Versions ({versions.length})
      </button>
      {open && (
        <ul className="mt-2 space-y-1 text-sm text-muted">
          {versions.map((v) => (
            // versions have no id of their own; supersededAt is unique per revision.
            <li key={v.supersededAt}>
              {pricingLabel(v.pricing, currency)} — superseded{' '}
              {new Date(v.supersededAt).toLocaleDateString()}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ConvertMenu({ quotationId }: { quotationId: string }): React.JSX.Element {
  const [target, setTarget] = useState<QuotationConvertTarget>('receivable');
  const [amount, setAmount] = useState('');
  const [open, setOpen] = useState(false);
  const convertMutation = useConvertQuotation(quotationId);

  return (
    <div className="rounded-[var(--radius-card)] border border-border bg-surface p-3">
      <button
        type="button"
        className={ghostButtonInlineClass}
        onClick={() => {
          setOpen((v) => !v);
        }}
      >
        Convert ▾
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <Field label="Convert to" htmlFor="convert-target">
            <select
              id="convert-target"
              className={selectClass}
              value={target}
              onChange={(e) => {
                setTarget(e.target.value as QuotationConvertTarget);
              }}
            >
              {CONVERT_TARGETS.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>
          {target === 'receivable' && (
            <Field
              label="Amount (leave blank to use the fixed price; required for a range)"
              htmlFor="convert-amount"
            >
              <input
                id="convert-amount"
                className={inputClass}
                inputMode="decimal"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                }}
              />
            </Field>
          )}
          <button
            type="button"
            className={primaryButtonInlineClass}
            disabled={convertMutation.isPending}
            onClick={() => {
              const amountMinor =
                amount.trim() !== '' ? Math.round(Number(amount) * 100) : undefined;
              convertMutation.mutate({
                target,
                ...(amountMinor !== undefined ? { amountMinor } : {}),
              });
            }}
          >
            Confirm convert
          </button>
          {convertMutation.isError && (
            <p className="text-sm text-danger">{convertMutation.error.message}</p>
          )}
        </div>
      )}
    </div>
  );
}

export function QuotationDetailPage(): React.JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { data: quotation, isLoading } = useQuotation(id);
  const sendMutation = useSendQuotation();
  const acceptMutation = useAcceptQuotation();
  const rejectMutation = useRejectQuotation();
  const expireMutation = useExpireQuotation();
  const reviseMutation = useRevisePricing(id);
  const [revising, setRevising] = useState(false);
  const [newAmount, setNewAmount] = useState('');

  if (isLoading) return <p className="text-sm text-muted">Loading…</p>;
  if (quotation === undefined) return <p className="text-sm text-muted">Quotation not found.</p>;

  const canRevise = quotation.status === 'draft' || quotation.status === 'sent';

  return (
    <div>
      <Link to="/quotes" className="mb-2 flex items-center gap-1 text-sm text-muted hover:text-ink">
        <ArrowLeft size={14} /> Quotations
      </Link>
      <h1 className="text-xl font-semibold text-ink">{quotation.title}</h1>
      <p className="mb-4 text-sm text-muted">
        {quotation.party} · {pricingLabel(quotation.pricing, quotation.currency)} ·{' '}
        <span className="capitalize">{quotation.status}</span>
      </p>

      <VersionsAccordion versions={quotation.versions} currency={quotation.currency} />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        {quotation.status === 'draft' && (
          <button
            type="button"
            className={primaryButtonInlineClass}
            disabled={sendMutation.isPending}
            onClick={() => {
              sendMutation.mutate(id);
            }}
          >
            Send
          </button>
        )}
        {quotation.status === 'sent' && (
          <>
            <button
              type="button"
              className={primaryButtonInlineClass}
              disabled={acceptMutation.isPending}
              onClick={() => {
                acceptMutation.mutate(id);
              }}
            >
              Accept
            </button>
            <button
              type="button"
              className={ghostButtonInlineClass}
              disabled={rejectMutation.isPending}
              onClick={() => {
                rejectMutation.mutate(id);
              }}
            >
              Reject
            </button>
            <button
              type="button"
              className={ghostButtonInlineClass}
              disabled={expireMutation.isPending}
              onClick={() => {
                expireMutation.mutate(id);
              }}
            >
              Mark expired
            </button>
          </>
        )}
        {quotation.status === 'accepted' && <ConvertMenu quotationId={id} />}
        <button
          type="button"
          className={ghostButtonInlineClass}
          onClick={() => {
            downloadQuotationPdf(quotation);
          }}
        >
          Share summary (PDF)
        </button>
      </div>

      {canRevise && (
        <div className="mb-4">
          {!revising ? (
            <button
              type="button"
              className={ghostButtonInlineClass}
              onClick={() => {
                setRevising(true);
              }}
            >
              Revise pricing
            </button>
          ) : (
            <div className="flex items-end gap-2">
              <Field label="New fixed amount" htmlFor="revise-amount">
                <AmountField
                  id="revise-amount"
                  currency={quotation.currency}
                  value={newAmount}
                  onChange={setNewAmount}
                />
              </Field>
              <button
                type="button"
                className={primaryButtonInlineClass}
                disabled={reviseMutation.isPending}
                onClick={() => {
                  const amountMinor = Math.round(Number(newAmount) * 100);
                  if (!(amountMinor > 0)) return;
                  reviseMutation.mutate(
                    { kind: 'fixed', amountMinor },
                    {
                      onSuccess: () => {
                        setRevising(false);
                        setNewAmount('');
                      },
                    },
                  );
                }}
              >
                Save revision
              </button>
              <button
                type="button"
                className={ghostButtonInlineClass}
                onClick={() => {
                  setRevising(false);
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {quotation.notes !== undefined && (
        <p className="text-sm text-muted">Notes: {quotation.notes}</p>
      )}
      {quotation.paymentTerms !== undefined && (
        <p className="mb-4 text-sm text-muted">Terms: {quotation.paymentTerms}</p>
      )}

      <EntityTagSection entityType="quotation" entityId={id} />
    </div>
  );
}
